/**
 * URL 参数监听 Hook
 * 处理邮件链接回调和 URL 参数唤起弹窗的场景
 *
 * 支持的 URL 参数：
 * - ?auth=signup - 打开注册弹窗
 * - ?auth=login - 打开登录弹窗
 * - ?auth=login&email=xxx - 打开登录弹窗并填充邮箱
 * - ?auth=login&email=xxx&code=xxx - 打开登录弹窗并填充邮箱（code 参数会被读取）
 * - ?auth=login&code=google_exist&email=xxx - Google OAuth 邮箱冲突回调（popup 内自动 postMessage 通知父窗口后关闭）
 * - ?auth=verify-email - 打开验证邮箱弹窗（链接唤起时不自动倒计时）
 * - ?auth=verify-email&email=xxx - 打开验证邮箱弹窗并填充邮箱
 * - ?auth=forgot - 打开忘记密码弹窗
 * - ?auth=verify - 邮箱验证回调（后端已处理验证并设置 Cookie，前端仅清除参数）
 * - ?auth=verify&mode=popup - Google OAuth 弹窗回调，处理完后自动关闭窗口
 * - ?auth=reset&cs=xxx - 重置密码回调
 * - ?auth=verify-failed - 验证失败弹窗
 * - ?auth=reset-failed - 重置密码失败弹窗
 */
import { useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from '@leadsnavi/ui-kit';
import type { AuthStep } from '@/components/auth-content/types';
import { authModalService } from '@/services/authModal';
import { authChannel } from './useAuthChannel';
import { isLoggedIn } from '@/utils/auth';
import useUserStore from '@/stores/userStore';

/** Google OAuth 邮箱冲突 postMessage 事件类型 */
export const GOOGLE_OAUTH_EXIST_TYPE = 'GOOGLE_OAUTH_EXIST';

interface GoogleOAuthExistMessage {
  type: typeof GOOGLE_OAUTH_EXIST_TYPE;
  email: string;
}

/** URL 参数到 AuthStep 的映射（verify 由后端直接处理，不再映射到弹窗步骤） */
const AUTH_PARAM_TO_STEP: Record<string, AuthStep> = {
  signup: 'signup',
  login: 'login',
  'verify-email': 'verify-email',
  forgot: 'forgot-password',
  reset: 'reset-password',
  'verify-failed': 'verify-failed',
  'reset-failed': 'reset-failed',
};

/** AuthStep 到 URL 参数的映射 */
const STEP_TO_AUTH_PARAM: Record<AuthStep, string> = {
  signup: 'signup',
  login: 'login',
  'verify-email': 'verify',
  'verify-failed': 'verify-failed',
  'forgot-password': 'forgot',
  'forgot-success': 'forgot',
  'reset-password': 'reset',
  'reset-failed': 'reset-failed',
};

/**
 * URL 参数监听 Hook
 * 自动监听 URL 参数变化并唤起对应的认证弹窗
 */
export function useAuthUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const processedRef = useRef<string | null>(null);
  // 保持 openAuthModal 的稳定引用，避免 message 监听 useEffect 频繁重绑定
  const openAuthModalRef = useRef<(step: AuthStep, cs?: string, email?: string) => Promise<void>>(async () => {});

  /**
   * 清除 auth 相关的 URL 参数
   */
  const clearAuthParams = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('auth');
    newParams.delete('mode');
    newParams.delete('cs');
    newParams.delete('email');
    newParams.delete('code');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * 设置 auth URL 参数
   */
  const setAuthParams = useCallback(
    (step: AuthStep, cs?: string) => {
      const newParams = new URLSearchParams(searchParams);
      const authParam = STEP_TO_AUTH_PARAM[step];

      if (authParam) {
        newParams.set('auth', authParam);
        if (cs) {
          newParams.set('cs', cs);
        } else {
          newParams.delete('cs');
        }
        setSearchParams(newParams, { replace: true });
      }
    },
    [searchParams, setSearchParams]
  );

  /**
   * 打开认证弹窗
   */
  const openAuthModal = useCallback(
    async (step: AuthStep, cs?: string, email?: string) => {
      try {
        await authModalService.open({
          step,
          cs,
          email,
          onClose: clearAuthParams,
          onLoginSuccess: clearAuthParams,
          onSignUpSuccess: clearAuthParams,
        });
      } catch {
        // 用户取消，清除参数
        clearAuthParams();
      }
    },
    [clearAuthParams]
  );

  // 同步 ref，确保 message 监听始终拿到最新版本
  useEffect(() => {
    openAuthModalRef.current = openAuthModal;
  }, [openAuthModal]);

  // 监听来自 Google OAuth popup 的 GOOGLE_OAUTH_EXIST 消息（父窗口侧）
  useEffect(() => {
    const handlePopupMessage = (event: MessageEvent<GoogleOAuthExistMessage>) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== GOOGLE_OAUTH_EXIST_TYPE) return;

      const email = event.data.email || '';

      toast.error('This account was registered via email. Please log in using your email and password.', {
        duration: 5000,
      });

      // 先关闭已有弹窗（无弹窗时为 no-op），再开新弹窗
      authModalService.close();
      requestAnimationFrame(() => {
        openAuthModalRef.current('login', undefined, email);
      });
    };

    window.addEventListener('message', handlePopupMessage);
    return () => window.removeEventListener('message', handlePopupMessage);
  }, []);

  // 监听 URL 参数变化
  useEffect(() => {
    const authParam = searchParams.get('auth');
    const modeParam = searchParams.get('mode');
    const csParam = searchParams.get('cs');
    const emailParam = searchParams.get('email');
    const codeParam = searchParams.get('code');

    // 如果没有 auth 参数，或者已经处理过相同的参数，跳过
    if (!authParam) {
      processedRef.current = null;
      return;
    }

    // 生成唯一标识，避免重复处理（包含 mode、email 和 code 参数）
    const paramKey = `${authParam}-${modeParam || ''}-${csParam || ''}-${emailParam || ''}-${codeParam || ''}`;
    if (processedRef.current === paramKey) {
      return;
    }
    processedRef.current = paramKey;

    if (authParam === 'verify') {
      authChannel.broadcastAuthSuccess();
      if (isLoggedIn()) {
        useUserStore.getState().fetchUser({ isAfterLoginSuccess: true });
      }
      clearAuthParams();

      // mode=popup 表示来自 Google OAuth 弹窗，处理完后自动关闭
      if (modeParam === 'popup') {
        window.close();
      }
      return;
    }

    // Google OAuth 邮箱冲突：该邮箱已在平台注册，需用密码登录
    // 此时当前窗口是 popup，通知 opener 打开登录弹窗后自行关闭
    if (authParam === 'login' && codeParam === 'google_exist') {
      if (window.opener && !window.opener.closed) {
        const message: GoogleOAuthExistMessage = {
          type: GOOGLE_OAUTH_EXIST_TYPE,
          email: emailParam || '',
        };
        window.opener.postMessage(message, window.location.origin);
      }
      window.close();
      return;
    }

    // 获取对应的步骤
    const step = AUTH_PARAM_TO_STEP[authParam];
    if (!step) {
      // 无效的 auth 参数，清除
      clearAuthParams();
      return;
    }

    // 重置密码需要 cs 参数
    if (authParam === 'reset' && !csParam) {
      console.warn(`Missing 'cs' parameter for auth=${authParam}`);
      openAuthModal('reset-failed');
      return;
    }

    // 打开弹窗，传递 email 参数（如果存在）
    openAuthModal(step, csParam || undefined, emailParam || undefined);
  }, [searchParams, clearAuthParams, openAuthModal]);

  return {
    /** 清除 auth URL 参数 */
    clearAuthParams,
    /** 设置 auth URL 参数 */
    setAuthParams,
    /** 手动打开认证弹窗 */
    openAuthModal,
    /** 当前的 auth 参数 */
    authParam: searchParams.get('auth'),
    /** 当前的 cs 参数 */
    csParam: searchParams.get('cs'),
    /** 当前的 email 参数 */
    emailParam: searchParams.get('email'),
    /** 当前的 code 参数 */
    codeParam: searchParams.get('code'),
    /** 当前的 mode 参数（popup 表示来自 OAuth 弹窗） */
    modeParam: searchParams.get('mode'),
  };
}

/**
 * 便捷方法：通过修改 URL 参数打开认证弹窗
 * 可在非 React 组件中使用
 */
export const authUrl = {
  /**
   * 跳转到注册页面
   */
  goToSignUp() {
    const url = new URL(window.location.href);
    url.searchParams.set('auth', 'signup');
    url.searchParams.delete('cs');
    window.history.pushState({}, '', url.toString());
    // 触发 popstate 事件让 React Router 感知变化
    window.dispatchEvent(new PopStateEvent('popstate'));
  },

  /**
   * 跳转到登录页面
   */
  goToLogin() {
    const url = new URL(window.location.href);
    url.searchParams.set('auth', 'login');
    url.searchParams.delete('cs');
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  },

  /**
   * 跳转到验证邮箱页面（链接唤起时不自动倒计时）
   * @param email 可选，预填邮箱
   */
  goToVerifyEmail(email?: string) {
    const url = new URL(window.location.href);
    url.searchParams.set('auth', 'verify-email');
    url.searchParams.delete('cs');
    if (email) {
      url.searchParams.set('email', email);
    } else {
      url.searchParams.delete('email');
    }
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  },

  /**
   * 跳转到忘记密码页面
   */
  goToForgotPassword() {
    const url = new URL(window.location.href);
    url.searchParams.set('auth', 'forgot');
    url.searchParams.delete('cs');
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  },

  /**
   * 清除 auth 参数
   */
  clearAuth() {
    const url = new URL(window.location.href);
    url.searchParams.delete('auth');
    url.searchParams.delete('cs');
    window.history.replaceState({}, '', url.toString());
  },
};

export default useAuthUrlParams;
